import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import Grid from "@mui/material/Grid";
import SaveIcon from "@mui/icons-material/Save";
import ColumnGroupingTable from "../../../table/ColumnGroupingTable.component";
import { useTreasuryKRISummaryContext } from "../../../../context/TreasuryKRISummaryContext";
import useStyles from "../NIISensitivityModeler.styles";
import { DIMENSION, MEASURE } from "../../../../utils/constants";

const calculateEarningsAtRisk = (data, columns) => {
  const niiChangeRow = data.find(
    (row) => row.type === "Change in NII caused by re-pricing",
  );

  if (!niiChangeRow) return 0;

  const monthColumns = columns.slice(2).map((col) => col.id);

  const sum = monthColumns.reduce((acc, monthId) => {
    const value = niiChangeRow[monthId];
    return acc + (typeof value === "number" ? value : 0);
  }, 0);

  return sum;
};

const NiiResultTable = ({
  niiOutputTablecolumn,
  niiOutputTableData,
  setNiiResultTableColumn,
  setNiiResultTableData,
  selectedDecimalsIn,
  selectedFigureDivisor,
  selectedMonthEnd,
  latestAnnualNIIForecastAmount,
  selectedBusinessEntityID,
  selectedModelDatabase,
  isWritableSfc,
  isAdmin,
  toSave,
  toCompare,
} = {}) => {
  const { classes } = useStyles();

  const { metricsHistoryUpsertAndSave, metricsHistoryUpsertAndSaveLoading } =
    useTreasuryKRISummaryContext();
  const [nii100BpParallelShiftPercentage, setNii100BpParallelShiftPercentage] =
    useState(null);
  const [latestAnnualNIIForecast, setLatestAnnualNIIForecast] = useState(null);
  const [twelveMonthsEarningAtRisk, setTwelveMonthsEarningAtRisk] =
    useState(null);

  const columns = useMemo(() => {
    return (
      [
        {
          label: `NII100bps Results for: ${selectedMonthEnd?.format("MM/DD/YYYY")}`,
          children: [
            {
              id: "name",
              label: "Name",
              minWidth: 140,
            },
            {
              id: "value",
              label: "Value",
              minWidth: 140,
              align: "right",
            },
          ],
        },
      ] || []
    );
  }, [selectedMonthEnd]);

  const datas = useMemo(() => {
    if (
      !niiOutputTableData ||
      niiOutputTableData.length === 0 ||
      !niiOutputTablecolumn ||
      niiOutputTablecolumn.length === 0
    ) {
      return [];
    }

    const earningsAtRisk = calculateEarningsAtRisk(
      niiOutputTableData,
      niiOutputTablecolumn,
    );
    const formattedEarningsAtRisk = parseFloat(earningsAtRisk.toFixed(2));

    const divisor = Number(selectedFigureDivisor) || 1;

    const niiForecast =
      parseFloat(
        (latestAnnualNIIForecastAmount / divisor)?.toFixed(selectedDecimalsIn),
      ) || 0;

    let parallelShiftPercentage = 0;

    if (niiForecast !== 0) {
      parallelShiftPercentage = (formattedEarningsAtRisk / niiForecast) * 100;
    }

    const formattedParallelShift = parallelShiftPercentage?.toFixed(2);
    setTwelveMonthsEarningAtRisk(formattedEarningsAtRisk);
    setLatestAnnualNIIForecast(niiForecast);
    setNii100BpParallelShiftPercentage(formattedParallelShift);

    return [
      {
        name: "12 months Earnings at Risk",
        value: formattedEarningsAtRisk,
        cellStyles: { fontWeight: "bold" },
        dynamicCellStyles: {
          value: {
            color: parseFloat(formattedEarningsAtRisk) < 0 ? "red" : "black",
          },
        },
      },
      {
        name: "Latest Annual NII Forecast",
        value: niiForecast,
        cellStyles: { fontWeight: "bold" },
        dynamicCellStyles: {
          value: {
            color: niiForecast < 0 ? "red" : "black",
          },
        },
      },
      {
        name: "100bp Parallel Shift (% Profit)",
        value: `${formattedParallelShift}%`,
        cellStyles: { fontWeight: "bold" },
        dynamicCellStyles: {
          value: {
            color: parallelShiftPercentage < 0 ? "red" : "black",
          },
        },
      },
    ];
  }, [
    niiOutputTableData,
    niiOutputTablecolumn,
    selectedFigureDivisor,
    latestAnnualNIIForecastAmount,
    selectedDecimalsIn,
  ]);

  useEffect(() => {
    if (datas) {
      setNiiResultTableColumn(columns);
      setNiiResultTableData(datas);
    }
  }, [columns, datas, setNiiResultTableColumn, setNiiResultTableData]);

  const handleSave100BpParallelShift = () => {
    if (
      parseInt(selectedFigureDivisor) === 1 &&
      parseInt(selectedDecimalsIn) === 0
    ) {
      metricsHistoryUpsertAndSave([
        {
          dimension: DIMENSION.INTEREST_RATE_RISK,
          dimension2: null,
          measure: MEASURE.TWELVE_MONTHS_EARNINGS_AT_RISK,
          businessEntityID: selectedBusinessEntityID,
          value: `${twelveMonthsEarningAtRisk}`,
          modelDatabase: selectedModelDatabase,
          isGlobal: false,
        },
        {
          dimension: DIMENSION.INTEREST_RATE_RISK,
          dimension2: null,
          measure: MEASURE.LATEST_ANNUAL_NII_FORECAST,
          businessEntityID: selectedBusinessEntityID,
          value: `${latestAnnualNIIForecast}`,
          modelDatabase: selectedModelDatabase,
          isGlobal: false,
        },
        {
          dimension: DIMENSION.INTEREST_RATE_RISK,
          dimension2: null,
          measure: MEASURE.HUNDRED_BP_PARALLEL_SHIFT_KRI,
          businessEntityID: selectedBusinessEntityID,
          value: `${nii100BpParallelShiftPercentage}%`,
          modelDatabase: selectedModelDatabase,
          isGlobal: false,
        },
      ]);
    } else {
      alert(
        "Please change the Figure Divisor selection to 'Raw' and Decimals selection to 'No Decimal' before saving.",
      );
    }
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          marginBottom: 0,
          width: "50%",
          margin: "0 auto",
          border: "1px solid #e0e0e0",
          height: "152px",
        }}
      >
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <ColumnGroupingTable
            columns={columns}
            datas={datas}
            loading={false}
            disablePagination={true}
            applyThousandsSeparator={true}
            tableHeight={185}
            hideChildRowHeader
          />
        </Grid>
      </Grid>
      {toSave && !toCompare && (isWritableSfc || isAdmin) && (
        <div className={classes.SaveBtn}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<SaveIcon />}
            onClick={handleSave100BpParallelShift}
            sx={{
              fontSize: "0.8rem",
              boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
              height: "40px",
            }}
            disabled={metricsHistoryUpsertAndSaveLoading}
          >
            Save Data
          </Button>
        </div>
      )}
    </>
  );
};

export default NiiResultTable;
